/**
 * Initializes ffmpeg in-browser - https://github.com/ffmpegwasm/ffmpeg.wasm#usage
 * This method is called once when download video is invoked
 * @returns ffmpeg instance
 */
// AY TODO: Would be ideal to move this out to "Encoder" functionality

var ffmpegLogs = [];
var setLogging = false;
class FFmpegOOMError extends Error {
  constructor(message = 'Failed to process the video due to insufficient memory') {
    super(message);
    this.name = 'FFmpegOOMError';
    this.code = 'FFMPEG_OOM';
  }
}

async function initFFmpeg() {
  // FFmpeg instance is globally added using script tag in the index.html page
  // https://github.com/ffmpegwasm/ffmpeg.wasm#installation > "Browser"

  const { createFFmpeg } = FFmpeg;
  var ffmpeg = createFFmpeg({ log: false });
  // eslint-disable-next-line
  console.log('Loading ffmpeg-core.js');
  if (!ffmpeg.isLoaded()) {
    await ffmpeg.load();
  }
  ffmpeg.setLogger(({ type, message }) => {
    if (!setLogging) {
      return;
    }
    ffmpegLogs.push(message);
  });
  return ffmpeg;
}

async function getVideoFPS(ffmpeg, file) {
  const { fetchFile } = FFmpeg;

  setFfmpegLogging(true);

  const data = await fetchFile(file);
  ffmpeg.FS('writeFile', file.name ?? 'input.mp4', data);

  await ffmpeg.run('-i', file.name ?? 'input.mp4');

  const logs = getFfmpegLogs();

  let fps = 0;

  for (let i = 0; i < logs.length; i++) {
    const fpsMatch = logs[i].match(/, (\d+(\.\d+)?) fps,/);

    if (fpsMatch) {
      fps = parseFloat(fpsMatch[1]);
      break;
    }
  };

  setFfmpegLogging(false);
  clearFfmpegLogs();

  return fps;
}

async function convertVideoFPS(ffmpeg, file, fps, progressCallback) {
  const outputFileName = 'output.mp4';
  setFfmpegLogging(true);

  // Create a promise that rejects when OOM is detected
  let oomReject;
  const oomPromise = new Promise((_, reject) => {
    oomReject = reject;
  });

  setProgressInLogging(ffmpeg, progressCallback, fps, false, (err) => {
    if (err === 'abort(OOM)') {
      oomReject(new FFmpegOOMError());
    }
  });

  let ffmpegCommand = ['-i', file.name ?? 'input.mp4'];

  // Re-encode video to avoid stream sync issues
  ffmpegCommand.push('-c:v', 'libx264');

  // Re-encode audio to avoid stream sync issues
  ffmpegCommand.push('-c:a', 'aac');

  // Use proper crf value to ensure output quality doesn't go down
  ffmpegCommand.push('-crf', '17');

  ffmpegCommand.push('-r', fps.toString(), outputFileName);

  try {
    // Race between ffmpeg execution and OOM detection
    await Promise.race([
      ffmpeg.run(...ffmpegCommand),
      oomPromise
    ]);

    const outputData = ffmpeg.FS('readFile', outputFileName);
    const blob = new Blob([outputData.buffer], { type: 'video/mp4' });
    return blob;
  } catch (error) {
    throw error; // Re-throw so caller can handle it
  } finally {
    setFfmpegLogging(false);
    clearFfmpegLogs();
  }
}

async function convertMovToTransparentWebm(ffmpeg, inputFile, progressCallback) {
  const { fetchFile } = FFmpeg;

  const inputName = "input.mov";
  const outputName = "output.webm";

  // Create a promise that rejects when OOM is detected
  let oomReject;
  const oomPromise = new Promise((_, reject) => {
    oomReject = reject;
  });

  setProgressInLogging(ffmpeg, progressCallback, 30, true,  (err) => {
    if (err === 'abort(OOM)') {
      oomReject(new FFmpegOOMError());
    }
  });

  // Write file into ffmpeg FS
  const inputData = await fetchFile(inputFile);
  ffmpeg.FS("writeFile", inputName, inputData);

  try {
    // Run ffmpeg
    await Promise.race([
      ffmpeg.run(
        "-i", inputName,
    
        "-vf", "fps=30",
        "-c:v", "libvpx-vp9",
    
        "-pix_fmt", "yuva420p",
        "-metadata:s:v:0", "alpha_mode=1",
    
        "-auto-alt-ref", "0",
        "-b:v", "0",
        "-crf", "18",
        "-row-mt", "1",
    
        "-c:a", "libopus",
        "-b:a", "192k",
    
        outputName
      ),
      oomPromise
    ])
  
    // Read result
    const data = ffmpeg.FS("readFile", outputName);
  
    const videoBlob = new Blob([data.buffer], { type: "video/webm" });
  
    return videoBlob;
  } catch (error) {
    throw error;
  } finally {
    setFfmpegLogging(false);
    clearFfmpegLogs();
  }
}

async function setProgressInLogging(ffmpeg, progressCallback, fps, convertingMovVideo, oomCallback) {
  let totalDuration = 0;
  ffmpeg.setLogger(({ type, message }) => {
    if (type == 'fferr') {
      if (message.includes('abort(OOM)')) {
        if (oomCallback) {
          oomCallback('abort(OOM)');
        }
      } else if (message.includes('Duration:')) {
        const durationMatch = message.match(/Duration: (\d+):(\d+):(\d+\.\d+)/);
        if (durationMatch) {
          const [hours, minutes, seconds] = durationMatch.slice(1).map(parseFloat);
          totalDuration = hours * 3600 + minutes * 60 + seconds;
        }
      } else if (message.includes('time=')) {
        const timeMatch = message.match(/time=(\d+):(\d+):(\d+\.\d+)/);
        if (timeMatch) {
          const [hours, minutes, seconds] = timeMatch.slice(1).map(parseFloat);
          const currentTimeInSeconds = hours * 3600 + minutes * 60 + seconds;
          if (totalDuration > 0) {
            const progress = (currentTimeInSeconds / totalDuration) * 100;
            if (progressCallback) {
              progressCallback({ progress, fps, convertingMovVideo });
            }
          }
        }
      }
    }
  });
}

const setFfmpegLogging = (value) => {
  setLogging = value;
}

function getFfmpegLogs() {
  return ffmpegLogs;
}

function clearFfmpegLogs() {
  ffmpegLogs = [];
}

module.exports = {
  initFFmpeg,
  setFfmpegLogging,
  getFfmpegLogs,
  clearFfmpegLogs,
  getVideoFPS,
  convertVideoFPS,
  convertMovToTransparentWebm
};